import type { UserNotification } from '../services/api/types';
import { stripHtmlToPlainText } from './plainText';

export type NotificationTabId = 'recent' | 'unread' | 'academic' | 'documents';

export type NotificationGroup = {
  id: string;
  label: string;
  items: UserNotification[];
};

export const NOTIFICATION_TABS: Array<{ id: NotificationTabId; label: string }> = [
  { id: 'recent', label: 'Most recent' },
  { id: 'unread', label: 'Unread' },
  { id: 'academic', label: 'Academic' },
  { id: 'documents', label: 'Documents' },
];

const ACADEMIC_KINDS = new Set([
  'assignment_created',
  'assignment_submitted',
  'announcement',
]);

const DOCUMENT_KINDS = new Set([
  'document_pdf_ready',
  'document_pdf_failed',
  'document_pdf_expired',
]);

/** On-demand PDF artifacts are deleted after download, or after 24 hours. */
export const DOCUMENT_PDF_READY_TTL_MS = 24 * 60 * 60 * 1000;

const SQLITE_UTC = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export function parseNotificationDate(value: string | null | undefined): Date | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const normalized = SQLITE_UTC.test(raw)
    ? `${raw.replace(' ', 'T')}${raw.endsWith('Z') ? '' : 'Z'}`
    : raw.includes(' ') && !raw.includes('T')
      ? raw.replace(' ', 'T')
      : raw;

  const parsed = Date.parse(normalized);
  if (!Number.isNaN(parsed)) return new Date(parsed);

  const fallback = Date.parse(raw);
  return Number.isNaN(fallback) ? null : new Date(fallback);
}

export function notificationTimestampMs(value: string | null | undefined): number {
  return parseNotificationDate(value)?.getTime() ?? 0;
}

export function formatNotificationWhen(
  value: string,
  nowMs: number = Date.now()
): { relative: string; exact: string } {
  const date = parseNotificationDate(value);
  if (!date) {
    return { relative: value, exact: value };
  }

  const exact = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

  const diffMs = nowMs - date.getTime();
  if (diffMs < 0) {
    return { relative: exact, exact };
  }

  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < 45_000) return { relative: 'Just now', exact };
  if (diffMs < hour) {
    const mins = Math.max(1, Math.round(diffMs / minute));
    return { relative: `${mins}m ago`, exact };
  }
  if (diffMs < day) {
    const hours = Math.max(1, Math.round(diffMs / hour));
    return { relative: `${hours}h ago`, exact };
  }

  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday.getTime() - day);
  if (date >= startOfYesterday && date < startOfToday) {
    return { relative: 'Yesterday', exact };
  }
  if (diffMs < 7 * day) {
    const days = Math.max(1, Math.round(diffMs / day));
    return { relative: `${days}d ago`, exact };
  }

  return {
    relative: new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date),
    exact,
  };
}

export function notificationKindLabel(kind: string): string {
  switch (kind) {
    case 'assignment_created':
      return 'Assignment';
    case 'assignment_submitted':
      return 'Submission';
    case 'announcement':
      return 'Announcement';
    case 'document_pdf_ready':
      return 'PDF ready';
    case 'document_pdf_expired':
      return 'PDF expired';
    case 'document_pdf_failed':
      return 'PDF failed';
    case 'staff_leave_requested':
      return 'Leave request';
    case 'staff_leave_approved':
      return 'Leave approved';
    case 'staff_leave_rejected':
      return 'Leave rejected';
    case 'pickup_pin_issued':
      return 'Pickup PIN';
    case 'communication_email':
      return 'Email';
    case 'password_reset':
      return 'Password';
    default:
      return kind.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
}

export function notificationKindIcon(kind: string): string {
  if (kind.startsWith('assignment_')) return 'fas fa-book-open';
  if (kind === 'announcement') return 'fas fa-bullhorn';
  if (kind.startsWith('document_pdf')) return 'fas fa-file-pdf';
  if (kind.startsWith('staff_leave')) return 'fas fa-calendar-check';
  if (kind === 'pickup_pin_issued') return 'fas fa-key';
  if (kind === 'communication_email') return 'fas fa-envelope';
  if (kind === 'password_reset') return 'fas fa-lock';
  return 'fas fa-bell';
}

export function isDocumentPdfDownloadable(
  item: Pick<UserNotification, 'kind' | 'entity_type' | 'entity_id' | 'created_at'>,
  nowMs: number = Date.now()
): boolean {
  if (item.kind !== 'document_pdf_ready') return false;
  if (item.entity_type !== 'bulk_job' || !item.entity_id) return false;
  const createdMs = notificationTimestampMs(item.created_at);
  if (!createdMs) return false;
  return nowMs - createdMs < DOCUMENT_PDF_READY_TTL_MS;
}

export function documentPdfNotificationHint(
  item: Pick<UserNotification, 'kind' | 'entity_type' | 'entity_id' | 'created_at'>,
  nowMs: number = Date.now()
): string {
  if (item.kind === 'document_pdf_failed') return '';
  if (item.kind === 'document_pdf_expired') return ' · Expired';
  if (item.kind !== 'document_pdf_ready') return '';
  return isDocumentPdfDownloadable(item, nowMs) ? ' · Tap to download' : ' · Expired';
}

export function displayNotificationText(value: string | null | undefined): string {
  return stripHtmlToPlainText(value);
}

export function sortNotificationsNewestFirst(items: UserNotification[]): UserNotification[] {
  return [...items].sort(
    (a, b) => notificationTimestampMs(b.created_at) - notificationTimestampMs(a.created_at)
  );
}

export function filterNotificationsByTab(
  items: UserNotification[],
  tab: NotificationTabId
): UserNotification[] {
  const sorted = sortNotificationsNewestFirst(items);
  switch (tab) {
    case 'unread':
      return sorted.filter((item) => !item.read_at);
    case 'academic':
      return sorted.filter((item) => ACADEMIC_KINDS.has(item.kind));
    case 'documents':
      return sorted.filter((item) => DOCUMENT_KINDS.has(item.kind));
    default:
      return sorted;
  }
}

export function emptyNotificationsMessage(tab: NotificationTabId): string {
  switch (tab) {
    case 'unread':
      return "You're all caught up";
    case 'academic':
      return 'No academic notifications';
    case 'documents':
      return 'No document notifications';
    default:
      return 'No notifications yet';
  }
}

export function groupNotificationsByRecency(
  items: UserNotification[],
  nowMs: number = Date.now()
): NotificationGroup[] {
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const todayStart = startOfToday.getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const weekStart = todayStart - 7 * 24 * 60 * 60 * 1000;

  const groups: NotificationGroup[] = [
    { id: 'today', label: 'Today', items: [] },
    { id: 'yesterday', label: 'Yesterday', items: [] },
    { id: 'week', label: 'This week', items: [] },
    { id: 'earlier', label: 'Earlier', items: [] },
  ];

  for (const item of sortNotificationsNewestFirst(items)) {
    const ts = notificationTimestampMs(item.created_at);
    if (ts >= todayStart) groups[0].items.push(item);
    else if (ts >= yesterdayStart) groups[1].items.push(item);
    else if (ts >= weekStart) groups[2].items.push(item);
    else groups[3].items.push(item);
  }

  return groups.filter((group) => group.items.length > 0);
}

export function tabCounts(items: UserNotification[]): Record<NotificationTabId, number> {
  return {
    recent: items.length,
    unread: items.filter((item) => !item.read_at).length,
    academic: items.filter((item) => ACADEMIC_KINDS.has(item.kind)).length,
    documents: items.filter((item) => DOCUMENT_KINDS.has(item.kind)).length,
  };
}
